import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useJobShipments } from "@/hooks/useJobShipments";
import { useAuth } from "@/lib/auth";
import { ShipmentCard } from "./ShipmentCard";
import { AddShipmentDialog } from "./AddShipmentDialog";
import { MilestoneCompletionReminderDialog } from "@/components/files/MilestoneCompletionReminderDialog";
import { CreateShipmentData, JobShipment } from "@/lib/types/job";
import { Plus, Truck, Package } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

interface ShippingTabProps {
  jobId: string;
  jobStatus?: string;
}

export const ShippingTab = ({ jobId, jobStatus }: ShippingTabProps) => {
  const { shipments, deliveryLocations, loading, createShipment, updateShipment, deleteShipment } = useJobShipments(jobId);
  const { user } = useAuth();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingShipment, setEditingShipment] = useState<JobShipment | null>(null);
  const [deletingShipmentId, setDeletingShipmentId] = useState<string | null>(null);
  const [showMilestoneReminder, setShowMilestoneReminder] = useState(false);

  const canManageShipments = user?.role === 'vendor' && jobStatus && ['awarded', 'in_progress', 'completed_pending_review'].includes(jobStatus);

  const handleSave = async (data: CreateShipmentData) => {
    if (editingShipment) {
      await updateShipment(editingShipment.id, data);
    } else {
      await createShipment(data);
      // Show milestone reminder after new shipment creation
      setShowMilestoneReminder(true);
    }
    setEditingShipment(null);
  };

  const handleEdit = (shipment: JobShipment) => {
    setEditingShipment(shipment);
    setShowAddDialog(true);
  };

  const handleDelete = async () => {
    if (deletingShipmentId) {
      await deleteShipment(deletingShipmentId);
      setDeletingShipmentId(null);
    }
  };

  const handleDialogClose = (open: boolean) => {
    if (!open) {
      setEditingShipment(null);
    }
    setShowAddDialog(open);
  };

  // Group shipments by delivery location
  const groupedShipments = deliveryLocations.map(location => ({
    location,
    shipments: shipments.filter(s => s.delivery_location_id === location.id)
  }));

  // Shipments without a specific location
  const unassignedShipments = shipments.filter(s => !s.delivery_location_id);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-muted-foreground">Loading shipments...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Truck className="h-5 w-5" />
          <h3 className="text-lg font-semibold">Shipping & Tracking</h3>
        </div>
        {canManageShipments && (
          <Button onClick={() => setShowAddDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Shipment
          </Button>
        )}
      </div>

      {shipments.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Package className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium mb-2">No shipments yet</p>
            <p className="text-sm text-muted-foreground text-center max-w-md">
              {canManageShipments 
                ? "Add tracking information once items ship to keep clients informed about delivery progress."
                : "The vendor will add tracking information once items ship."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {groupedShipments.map(({ location, shipments: locationShipments }) => (
            locationShipments.length > 0 && (
              <div key={location.id}>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base font-medium">
                      {location.delivery_name}
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">
                      {location.city}, {location.state_province}
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {locationShipments.map((shipment) => (
                      <ShipmentCard
                        key={shipment.id}
                        shipment={shipment}
                        deliveryLocation={location}
                        canEdit={canManageShipments || false}
                        onEdit={handleEdit}
                        onDelete={(id) => setDeletingShipmentId(id)}
                      />
                    ))}
                  </CardContent>
                </Card>
              </div>
            )
          ))}

          {unassignedShipments.length > 0 && (
            <div>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base font-medium">
                    General Shipments
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {unassignedShipments.map((shipment) => (
                    <ShipmentCard
                      key={shipment.id}
                      shipment={shipment}
                      canEdit={canManageShipments || false}
                      onEdit={handleEdit}
                      onDelete={(id) => setDeletingShipmentId(id)}
                    />
                  ))}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      )}

      <AddShipmentDialog
        open={showAddDialog}
        onOpenChange={handleDialogClose}
        deliveryLocations={deliveryLocations}
        onSave={handleSave}
        editingShipment={editingShipment}
      />

      <AlertDialog open={!!deletingShipmentId} onOpenChange={(open) => !open && setDeletingShipmentId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Shipment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this shipment? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <MilestoneCompletionReminderDialog
        open={showMilestoneReminder}
        onOpenChange={setShowMilestoneReminder}
        jobId={jobId}
        userRole="vendor"
        uploadedCategory="shipment"
      />
    </div>
  );
};
